import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import {
  Upload, FileText, Image as ImageIcon, Database, Trash2, Eye, Edit2,
  Search, Sparkles, Activity, Loader2, CheckCircle2, XCircle, X, Tag, Download,
  ChevronRight, ChevronDown, Camera,
} from "lucide-react";

const fmtSize = (b) => {
  if (!b) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch { return s; }
};

const extIcon = (f) => {
  const e = (f?.ext || "").toLowerCase();
  if (["jpg", "jpeg", "png", "webp"].includes(e)) return <ImageIcon className="w-4 h-4 text-blue-600" />;
  if (e === "pdf") return <FileText className="w-4 h-4 text-red-600" />;
  if (e === "bc3") return <Database className="w-4 h-4 text-orange-600" />;
  return <FileText className="w-4 h-4 text-slate-500" />;
};

function StatusBadge({ status }) {
  if (!status) return null;
  const map = {
    completed: ["bg-green-100", "text-green-800", "Completado"],
    processing: ["bg-blue-100", "text-blue-800", "Procesando"],
    error: ["bg-red-100", "text-red-800", "Error"],
  };
  const [bg, fg, lbl] = map[status] || ["bg-slate-100", "text-slate-800", status];
  return <span className={`text-xs px-2 py-0.5 ${bg} ${fg} font-semibold`}>{lbl}</span>;
}

function PreviewModal({ file, token, onClose }) {
  if (!file) return null;
  const url = `${API_BASE}/files/by-id/${file.id}`;
  const isImg = ["jpg", "jpeg", "png", "webp"].includes((file.ext || "").toLowerCase());
  const isPdf = (file.ext || "").toLowerCase() === "pdf";
  const isBc3 = (file.ext || "").toLowerCase() === "bc3";
  const previewUrl = `${url}?ap_token=${token}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="preview-modal">
      <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="font-semibold">{file.display_name || file.name}</div>
            <div className="text-xs text-slate-500">{file.name} · {fmtSize(file.size)}</div>
          </div>
          <button data-testid="preview-close" className="p-2" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100">
          {isImg && (
            <img alt={file.name} src={previewUrl} className="max-w-full mx-auto" />
          )}
          {isPdf && (
            <iframe title={file.name} src={previewUrl} className="w-full h-[75vh]" />
          )}
          {isBc3 && (
            <div className="p-6 text-sm text-slate-700">
              <div className="bg-white p-4 border border-slate-200">
                <div className="font-semibold mb-2 flex items-center gap-2"><Database className="w-4 h-4 text-orange-600" /> Archivo BC3 (FIEBDC-3)</div>
                <p className="text-slate-600">Este es un archivo de presupuesto estándar BC3. Pulsa "Procesar BC3" en la lista para extraer su árbol de capítulos y partidas.</p>
                {file.bc3_data && <Bc3Tree tree={file.bc3_data} />}
              </div>
            </div>
          )}
          {!isImg && !isPdf && !isBc3 && (
            <div className="p-12 text-center text-slate-500">No hay vista previa para este tipo de archivo.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Bc3Tree({ tree }) {
  const [openMap, setOpenMap] = useState({});
  const toggle = (code) => setOpenMap((m) => ({ ...m, [code]: !m[code] }));
  const renderNode = (n, depth) => {
    const code = n.codigo;
    const isCap = n.es_capitulo;
    const open = openMap[code] !== false;
    const hijos = n.hijos || [];
    return (
      <div key={code} className="border-l border-slate-200 ml-2 my-0.5" style={{ paddingLeft: 8 }}>
        <button
          onClick={() => hijos.length && toggle(code)}
          className={"flex items-start gap-1 text-left w-full hover:bg-slate-50 px-1 py-0.5 " + (isCap ? "font-semibold text-slate-900" : "text-slate-700")}
          data-testid={"bc3-node-" + code}
        >
          {hijos.length ? (open ? <ChevronDown className="w-3.5 h-3.5 mt-1" /> : <ChevronRight className="w-3.5 h-3.5 mt-1" />) : <span className="w-3.5" />}
          <span className="text-xs font-mono text-slate-500 mt-0.5">{code}</span>
          <span className="flex-1 text-sm">{n.titulo}</span>
          {!isCap && (
            <span className="text-xs text-slate-500">
              {(n.cantidad || 1).toString()} {n.unidad || "ud"} · {(n.precio || 0).toFixed(2)}{"€"}
            </span>
          )}
        </button>
        {open && hijos.length > 0 && (
          <div>{hijos.map((h) => renderNode(h, depth + 1))}</div>
        )}
      </div>
    );
  };
  if (!tree?.capitulos?.length) return <div className="text-xs text-slate-500 mt-2">Sin capítulos.</div>;
  return (
    <div className="mt-3 max-h-[60vh] overflow-auto bg-slate-50 p-2 border border-slate-200">
      {tree.capitulos.map((c) => renderNode(c, 0))}
    </div>
  );
}

function OcrResultModal({ file, onClose }) {
  if (!file) return null;
  const data = file.ocr_data || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-600" /> Resultado OCR · {file.display_name || file.name}</div>
          <button className="p-2" onClick={onClose} data-testid="ocr-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-auto p-4 text-sm">
          {file.ocr_warnings?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 p-3 mb-3 text-xs">
              <div className="font-semibold mb-1">Auto-corrección aplicada:</div>
              <ul className="list-disc pl-4">{file.ocr_warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              ["Proveedor", data.proveedor],
              ["NIF/CIF", data.supplier_tax_id],
              ["Dirección", data.supplier_address],
              ["IBAN", data.iban],
              ["Fecha", data.fecha],
              ["Vencimiento", data.fecha_vencimiento],
              ["Nº factura", data.numero_factura],
              ["Categoría", data.categoria],
              ["Base", data.base_imponible],
              ["IVA %", data.tax_rate],
              ["IVA €", data.impuestos],
              ["Total", data.total],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-xs text-slate-500">{k}</div>
                <div className="font-medium break-words">{v ?? "—"}</div>
              </div>
            ))}
          </div>
          {Array.isArray(data.lineas) && data.lineas.length > 0 && (
            <div className="mb-3">
              <div className="font-semibold mb-1">Líneas detectadas</div>
              <table className="w-full text-xs border border-slate-200">
                <thead className="bg-slate-50"><tr><th className="text-left p-1">Concepto</th><th className="p-1">Cant.</th><th className="p-1">Precio</th><th className="p-1">Total</th></tr></thead>
                <tbody>
                  {data.lineas.map((l, i) => (
                    <tr key={i} className="border-t border-slate-200">
                      <td className="p-1">{l.concepto}</td>
                      <td className="p-1 text-right">{l.cantidad}</td>
                      <td className="p-1 text-right">{l.precio}</td>
                      <td className="p-1 text-right">{l.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.texto_completo && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-slate-500">Texto completo</summary>
              <pre className="text-xs whitespace-pre-wrap bg-slate-50 p-2 mt-1 max-h-60 overflow-auto">{data.texto_completo}</pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadDialog({ onClose, onDone, hint }) {
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const isBc3Hint = hint === "bc3";
  const acceptAttr = isBc3Hint ? ".bc3" : ".pdf,.jpg,.jpeg,.png,.webp,.bc3,.docx,.xlsx,.txt";

  const choose = (f) => {
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Máximo 20 MB");
      return;
    }
    setFile(f);
    setDisplayName(f.name);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    choose(f);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t) && tags.length < 20) setTags([...tags, t]);
    setTagInput("");
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    if (displayName) fd.append("display_name", displayName);
    if (tags.length) fd.append("tags", JSON.stringify(tags));
    try {
      await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast.success("Archivo subido correctamente");
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="upload-dialog">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold">{isBc3Hint ? "Subir archivo BC3" : "Subir archivo"}</div>
          <button onClick={onClose} className="p-2"><X className="w-5 h-5" /></button>
        </div>
        {isBc3Hint && (
          <div className="bg-orange-50 border border-orange-200 text-orange-900 text-xs p-3 mb-3">
            <strong>Selecciona un archivo .bc3</strong> (formato FIEBDC-3 que usan arquitectos y aparejadores). Después podrás convertirlo en presupuesto en 1 clic.
          </div>
        )}
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={`block border-2 border-dashed p-6 text-center cursor-pointer ${drag ? "border-orange-500 bg-orange-50" : "border-slate-300"}`}
          data-testid="upload-dropzone"
        >
          <Upload className="w-8 h-8 mx-auto text-slate-400" />
          <div className="mt-2 text-sm">{isBc3Hint ? "Arrastra el .bc3 aquí o haz clic" : "Arrastra aquí o haz clic para seleccionar"}</div>
          <div className="text-xs text-slate-500 mt-1">{isBc3Hint ? "Sólo archivos .bc3 (máx 20 MB)" : "PDF · Imágenes · BC3 · Office (máx 20 MB)"}</div>
          <input
            type="file"
            data-testid="upload-input"
            className="hidden"
            accept={acceptAttr}
            onChange={(e) => choose(e.target.files?.[0])}
          />
        </label>
        {file && (
          <div className="mt-3 text-xs text-slate-700 bg-slate-50 p-2">{file.name} · {fmtSize(file.size)}</div>
        )}
        <div className="mt-3">
          <label className="text-xs text-slate-500">Nombre visible</label>
          <input
            data-testid="upload-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="mt-3">
          <label className="text-xs text-slate-500">Etiquetas</label>
          <div className="flex flex-wrap gap-1 mb-2">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-xs bg-slate-200 px-2 py-0.5">
                {t}
                <button onClick={() => setTags(tags.filter((x) => x !== t))}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="upload-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              placeholder="proveedor, factura, 2026…"
              className="flex-1 border border-slate-300 px-3 py-2 text-sm"
            />
            <button onClick={addTag} type="button" className="px-3 bg-slate-900 text-white text-sm">Añadir</button>
          </div>
        </div>
        {busy && (
          <div className="mt-3">
            <div className="h-2 bg-slate-200 overflow-hidden">
              <div className="h-full bg-orange-600" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-xs text-slate-500 mt-1">{progress}%</div>
          </div>
        )}
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm">Cancelar</button>
          <button
            data-testid="upload-confirm"
            onClick={upload}
            disabled={!file || busy}
            className="px-4 py-2 bg-orange-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Subir archivo"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AiJobs({ refreshKey }) {
  const [data, setData] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ai/jobs");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]);
  return (
    <div className="bg-white border border-slate-200 p-4" data-testid="ai-jobs-section">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-orange-600" /> Procesos IA</div>
        <button onClick={load} className="text-xs text-slate-500 hover:text-slate-900" data-testid="ai-jobs-refresh">{loading ? "…" : "Actualizar"}</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div className="bg-slate-50 p-2 text-center"><div className="text-xs text-slate-500">Total</div><div className="font-bold">{data.stats?.total || 0}</div></div>
        <div className="bg-slate-50 p-2 text-center"><div className="text-xs text-slate-500">OK</div><div className="font-bold text-green-700">{data.stats?.completed || 0}</div></div>
        <div className="bg-slate-50 p-2 text-center"><div className="text-xs text-slate-500">Errores</div><div className="font-bold text-red-700">{data.stats?.errors || 0}</div></div>
        <div className="bg-slate-50 p-2 text-center"><div className="text-xs text-slate-500">Tiempo medio</div><div className="font-bold">{(data.stats?.avg_ms || 0)} ms</div></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-2">Tipo</th>
              <th className="text-left p-2">Archivo</th>
              <th className="text-left p-2">Estado</th>
              <th className="text-right p-2">ms</th>
              <th className="text-left p-2">Cuándo</th>
              <th className="text-left p-2">Notas</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).slice(0, 30).map((j) => (
              <tr key={j.id} className="border-t border-slate-100">
                <td className="p-2 font-mono">{j.type}</td>
                <td className="p-2">{j.file_name || j.file_id?.slice(0, 8)}</td>
                <td className="p-2">{j.status === "completed" ? <span className="text-green-700"><CheckCircle2 className="w-3 h-3 inline" /> OK</span> : <span className="text-red-700"><XCircle className="w-3 h-3 inline" /> Error</span>}</td>
                <td className="p-2 text-right">{j.ms || "—"}</td>
                <td className="p-2 text-slate-500">{fmtDate(j.created_at)}</td>
                <td className="p-2 text-slate-500 truncate max-w-[200px]">{j.error || (j.warnings?.length ? `${j.warnings.length} avisos` : "")}</td>
              </tr>
            ))}
            {(!data.items || data.items.length === 0) && (
              <tr><td colSpan={6} className="text-center text-slate-500 p-4">Aún no hay procesos IA</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Files() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [ocrFile, setOcrFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editVals, setEditVals] = useState({ display_name: "", tags: "" });
  const [busyId, setBusyId] = useState(null);
  const [aiJobsRefresh, setAiJobsRefresh] = useState(0);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("ap_token") || "";
  const uploadHint = params.get("hint") || "";

  // Auto-open upload dialog when ?upload=1
  useEffect(() => {
    if (params.get("upload") === "1") {
      setShowUpload(true);
      const p = new URLSearchParams(params);
      p.delete("upload");
      setParams(p, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/files");
      setFiles(r.data || []);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    files.forEach((f) => (f.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [files]);

  const filtered = useMemo(() => {
    return files.filter((f) => {
      const e = (f.ext || "").toLowerCase();
      if (filter === "image" && !["jpg", "jpeg", "png", "webp"].includes(e)) return false;
      if (filter === "pdf" && e !== "pdf") return false;
      if (filter === "bc3" && e !== "bc3") return false;
      if (filter === "other" && ["jpg", "jpeg", "png", "webp", "pdf", "bc3"].includes(e)) return false;
      if (tagFilter && !(f.tags || []).includes(tagFilter)) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = `${f.display_name || ""} ${f.name || ""} ${(f.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [files, filter, tagFilter, search]);

  const runOcr = async (f, mode = "full") => {
    setBusyId(f.id);
    toast.info(`OCR ${mode === "fast" ? "rápido" : "completo"} en marcha…`);
    try {
      const r = await api.post("/ocr/process", { file_id: f.id, mode });
      toast.success("OCR completado");
      await load();
      setAiJobsRefresh((v) => v + 1);
      const updated = (await api.get("/files")).data.find((x) => x.id === f.id);
      if (updated) setOcrFile(updated);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.upgrade_required) {
        // Modal global se encarga del upsell
      } else {
        toast.error(err?.response?.data?.detail || "Error en OCR");
      }
    } finally { setBusyId(null); }
  };

  const runBc3 = async (f) => {
    setBusyId(f.id);
    toast.info("Procesando BC3…");
    try {
      const fd = new FormData();
      fd.append("file_id", f.id);
      const r = await api.post("/bc3/process", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`BC3 procesado · ${r.data?.tree?.capitulos?.length || 0} capítulos`);
      await load();
      setAiJobsRefresh((v) => v + 1);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.upgrade_required) {
        // Modal global se encarga del upsell
      } else {
        toast.error(err?.response?.data?.detail || "Error procesando BC3");
      }
    } finally { setBusyId(null); }
  };

  const runPdfToBudget = async (f) => {
    const clientName = window.prompt("Nombre del cliente (opcional, se intentará detectar del PDF):", "");
    setBusyId(f.id);
    toast.info("Leyendo PDF y generando presupuesto…", { duration: 8000 });
    try {
      const { data } = await api.post("/budgets/from-pdf", {
        file_id: f.id,
        client_name: clientName || undefined,
      });
      toast.success(`Presupuesto creado · ${data.ai_data?.items?.length || 0} partidas`);
      navigate(`/budgets/${data.id}/edit`);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.upgrade_required) {
        // Modal global se encarga
      } else {
        toast.error(err?.response?.data?.detail || "Error generando desde PDF");
      }
    } finally { setBusyId(null); }
  };

  const removeFile = async (f) => {
    if (!window.confirm(`¿Eliminar "${f.display_name || f.name}"?`)) return;
    try {
      await api.delete(`/files/by-id/${f.id}`);
      toast.success("Archivo eliminado");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const startEdit = (f) => {
    setEditing(f);
    setEditVals({ display_name: f.display_name || f.name, tags: (f.tags || []).join(", ") });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/files/by-id/${editing.id}`, {
        display_name: editVals.display_name,
        tags: editVals.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch { toast.error("Error guardando"); }
  };

  return (
    <div className="space-y-6" data-testid="files-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="kbar">Facturas, tickets, planos y BC3</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-1">Tus archivos</h1>
          <p className="text-slate-500 text-sm mt-1">Sube cualquier documento. Si es una factura, la IA la lee sola. Si es un BC3, lo convertimos en presupuesto.</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="bg-orange-600 text-white px-5 h-12 text-sm font-semibold flex items-center gap-2 hover:bg-orange-700"
          data-testid="files-upload-btn"
        >
          <Upload className="w-4 h-4" /> Subir archivo
        </button>
      </div>

      {/* Empty state enriched */}
      {!loading && files.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="files-empty-state">
          <button
            onClick={() => setShowUpload(true)}
            className="group bg-white border-2 border-dashed border-slate-300 hover:border-orange-600 p-6 text-left transition-colors"
            data-testid="empty-cta-invoice"
          >
            <div className="h-12 w-12 bg-orange-50 flex items-center justify-center mb-3">
              <Camera className="w-6 h-6 text-orange-600" />
            </div>
            <div className="font-bold text-lg">Sube una factura</div>
            <p className="text-xs text-slate-500 mt-1">Haz una foto o arrastra el PDF. La IA extrae proveedor, NIF, base, IVA, total y fecha.</p>
            <div className="text-xs text-orange-600 font-semibold mt-3 group-hover:underline">Subir ahora →</div>
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="group bg-white border-2 border-dashed border-slate-300 hover:border-orange-600 p-6 text-left transition-colors"
            data-testid="empty-cta-bc3"
          >
            <div className="h-12 w-12 bg-orange-50 flex items-center justify-center mb-3">
              <Database className="w-6 h-6 text-orange-600" />
            </div>
            <div className="font-bold text-lg">Importa un BC3</div>
            <p className="text-xs text-slate-500 mt-1">¿Tienes un .bc3 del arquitecto? Súbelo y lo convertimos en presupuesto con 1 clic.</p>
            <div className="text-xs text-orange-600 font-semibold mt-3 group-hover:underline">Subir .bc3 →</div>
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="group bg-white border-2 border-dashed border-slate-300 hover:border-orange-600 p-6 text-left transition-colors"
            data-testid="empty-cta-docs"
          >
            <div className="h-12 w-12 bg-orange-50 flex items-center justify-center mb-3">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div className="font-bold text-lg">Guarda documentos</div>
            <p className="text-xs text-slate-500 mt-1">PDF, fotos, Word, Excel, planos… hasta 20 MB por archivo, siempre accesibles.</p>
            <div className="text-xs text-orange-600 font-semibold mt-3 group-hover:underline">Subir archivo →</div>
          </button>
        </div>
      ) : (
      <>
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <input
            data-testid="files-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o etiqueta…"
            className="w-full pl-8 pr-3 py-2 border border-slate-300 text-sm"
          />
        </div>
        <select
          data-testid="files-filter-type"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-slate-300 px-2 py-2 text-sm"
        >
          <option value="all">Todos los tipos</option>
          <option value="image">Imágenes</option>
          <option value="pdf">PDF</option>
          <option value="bc3">BC3</option>
          <option value="other">Otros</option>
        </select>
        {allTags.length > 0 && (
          <select
            data-testid="files-filter-tag"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="border border-slate-300 px-2 py-2 text-sm"
          >
            <option value="">Todas las etiquetas</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <button onClick={load} className="text-xs text-slate-500 hover:text-slate-900 px-2">Refrescar</button>
      </div>

      {/* Files table */}
      <div className="bg-white border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3">Archivo</th>
                <th className="text-left p-3 hidden md:table-cell">Etiquetas</th>
                <th className="text-right p-3 hidden md:table-cell">Tamaño</th>
                <th className="text-left p-3 hidden md:table-cell">Subido</th>
                <th className="text-left p-3">IA</th>
                <th className="text-right p-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">Ningún archivo coincide con tu búsqueda</td></tr>
              )}
              {filtered.map((f) => {
                const isImage = ["jpg", "jpeg", "png", "webp"].includes((f.ext || "").toLowerCase());
                const isPdf = (f.ext || "").toLowerCase() === "pdf";
                const isBc3 = (f.ext || "").toLowerCase() === "bc3";
                return (
                  <tr key={f.id} className="border-t border-slate-100" data-testid={`file-row-${f.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {extIcon(f)}
                        <div className="min-w-0">
                          <div className="font-medium truncate max-w-[260px]" title={f.display_name || f.name}>{f.display_name || f.name}</div>
                          <div className="text-xs text-slate-500 truncate">{f.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(f.tags || []).map((t) => <span key={t} className="text-xs bg-slate-100 px-2 py-0.5">{t}</span>)}
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell text-right text-slate-500">{fmtSize(f.size)}</td>
                    <td className="p-3 hidden md:table-cell text-slate-500 text-xs">{fmtDate(f.created_at)}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-0.5">
                        {f.ocr_status && <div className="flex items-center gap-1 text-xs">OCR: <StatusBadge status={f.ocr_status} /></div>}
                        {f.bc3_status && <div className="flex items-center gap-1 text-xs">BC3: <StatusBadge status={f.bc3_status} /></div>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {(isImage || isPdf || isBc3) && (
                          <button
                            data-testid={`file-preview-${f.id}`}
                            onClick={() => setPreviewFile(f)}
                            className="p-1.5 hover:bg-slate-100"
                            title="Vista previa"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {(isImage || isPdf) && (
                          <>
                            <button
                              data-testid={`file-ocr-fast-${f.id}`}
                              disabled={busyId === f.id}
                              onClick={() => runOcr(f, "fast")}
                              className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
                              title="OCR rápido (sólo texto)"
                            >
                              {busyId === f.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "OCR rápido"}
                            </button>
                            <button
                              data-testid={`file-ocr-full-${f.id}`}
                              disabled={busyId === f.id}
                              onClick={() => runOcr(f, "full")}
                              className="text-xs px-2 py-1 bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                              title="OCR completo (IA estructurada)"
                            >
                              {busyId === f.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "OCR IA"}
                            </button>
                            {f.ocr_status === "completed" && (
                              <button
                                data-testid={`file-ocr-view-${f.id}`}
                                onClick={() => setOcrFile(f)}
                                className="text-xs px-2 py-1 bg-green-100 text-green-800 hover:bg-green-200"
                              >
                                Ver OCR
                              </button>
                            )}
                            {isPdf && (
                              <button
                                data-testid={`file-pdf-to-budget-${f.id}`}
                                disabled={busyId === f.id}
                                onClick={() => runPdfToBudget(f)}
                                title="La IA leerá el PDF y creará un presupuesto"
                                className="text-xs px-2 py-1 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1"
                              >
                                {busyId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                Generar presupuesto
                              </button>
                            )}
                          </>
                        )}
                        {isBc3 && (
                          <>
                            <button
                              data-testid={`file-bc3-process-${f.id}`}
                              disabled={busyId === f.id}
                              onClick={() => runBc3(f)}
                              className="text-xs px-2 py-1 bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                            >
                              {busyId === f.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Procesar BC3"}
                            </button>
                            {f.bc3_status === "completed" && (
                              <>
                                <a
                                  data-testid={`file-bc3-viewer-${f.id}`}
                                  href={`/bc3-viewer/${f.id}`}
                                  className="text-xs px-2 py-1 bg-slate-900 text-white hover:bg-slate-800"
                                  title="Ver árbol jerárquico con mediciones"
                                >
                                  Ver árbol
                                </a>
                                <a
                                  data-testid={`file-bc3-import-${f.id}`}
                                  href={`/bc3-import?file_id=${f.id}`}
                                  className="text-xs px-2 py-1 bg-green-100 text-green-800 hover:bg-green-200"
                                >
                                  Importar
                                </a>
                              </>
                            )}
                          </>
                        )}
                        <a
                          data-testid={`file-download-${f.id}`}
                          href={`${API_BASE}/files/by-id/${f.id}?ap_token=${token}`}
                          className="p-1.5 hover:bg-slate-100"
                          title="Descargar"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          data-testid={`file-edit-${f.id}`}
                          onClick={() => startEdit(f)}
                          className="p-1.5 hover:bg-slate-100"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`file-delete-${f.id}`}
                          onClick={() => removeFile(f)}
                          className="p-1.5 hover:bg-red-100 text-red-600"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AiJobs refreshKey={aiJobsRefresh} />
      </>
      )}

      {showUpload && <UploadDialog onClose={() => setShowUpload(false)} onDone={load} hint={uploadHint} />}
      {previewFile && <PreviewModal file={previewFile} token={token} onClose={() => setPreviewFile(null)} />}
      {ocrFile && <OcrResultModal file={ocrFile} onClose={() => setOcrFile(null)} />}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-semibold">Editar archivo</div>
              <button onClick={() => setEditing(null)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500">Nombre visible</label>
                <input
                  data-testid="edit-display-name"
                  value={editVals.display_name}
                  onChange={(e) => setEditVals({ ...editVals, display_name: e.target.value })}
                  className="w-full border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Etiquetas (separadas por coma)</label>
                <input
                  data-testid="edit-tags"
                  value={editVals.tags}
                  onChange={(e) => setEditVals({ ...editVals, tags: e.target.value })}
                  className="w-full border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="flex gap-2 justify-end mt-3">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancelar</button>
                <button data-testid="edit-save" onClick={saveEdit} className="px-4 py-2 bg-orange-600 text-white text-sm font-semibold">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
